let md = `## 引子
1. 批判性思维的定义（概述的内容）![[附件/Pasted image 20230604020420.png]]
2. 反思是批判性思维最本质的特征

## 一、反思的概念
1. “反思性思维”是相对于“对象性思维”而言的，并且是基于对象性思维之上的。
	1. 对象性思维：对象性思维就是我们的注意力主要放在对象上的那种思维形式。
	2. 以思维作为思维对象的思维形式黑格尔称为Nachdenken（反思、深思）：反思就是以思想的本身为内容，力求思想<u>自觉其为思想</u>。
		1. 自觉其为思想：我知道自己在想什么，我是怎么想的 
		2. 在对问题进行思考之后，重新以刚才的思考过程作为目标进行思考
		3. 按此定义，对象性思维是<u>非自觉的思维</u>
	3. 思考的倾向不同
		1. 对象性思维：我 思考 <u>某事</u> （关注的是对象）
		2. 反思性思维：<u>我 思考</u> 某事 （关注的是我是怎么思考的。我的思考、思考的我）
	4. 举例：小学生择校。
		1. 思维根基性的要素：目的、问题、概念
		2. 我们要对我们思考本身进行思考
2. 必须关注思考主体的“我”
	1. 在我们日常思维和语言当中，作为思维主体的“我”很容易被忽视，甚至最终被剔除，以推卸自己的责任获得一种虚假的客观性。
	2. 很多时候，问题的根源不在外部，而在我们自身。
	3. 例子一：“这个世界上为什么找不到一个可爱的女人？”（思维的主体是你，原因可能在你自身）
	4. 例子二：企业老板感觉跟中高层干部没有话聊，很闷，企业没有活力。
		1. 你有没有为整个企业提供一个公共的话题，让大家互相之间有共同话题？
		2. 企业文化应该是能激发员工热烈讨论的内容，这样才能营造讨论的氛围
3. 我的反思和再造
	1. 我的思考肯定是“我”的思考，我是怎样的人，我就会做怎样的思考；我这个人“有问题”，我的思考也必然随之“有问题”
	2. 你要研究的不是股票，而是你自己（自己的贪婪和恐惧）。你要寻找的不是工作，而是你自己（自己是什么样的人、有什么特长？）。
	3. 思维训练的关键在于自我的反思与再造。为此，可以多读文学、哲学、心理学、宗教方面的书籍。人与人之间思维境界有差距，实际上是人的境界之间的差距。
	4. 苏格拉底：认识你自己。
	5. 孟子：反求诸己。
4. 总结
	1. 对象性思维：本能性的、偏见性的思考。且自以为是。
	2. 反思性思维：需要深刻的自我反思和系统的思考技能。



## 二、反思的意义

1. 佛教：初念浅，转念深。问题练习：你认为“初念”和“转念”有哪些不同？
	1. 参考答案：![[附件/Pasted image 20230604133704.png]]
2. 故事一：以明很特别，在一块钱和五毛钱之间，总是只选择五毛钱。
	1. 初念：真的有这种人？我要去测试下。以明真傻
	2. 转念：（以明视角）我如果选择了一块钱，以后就不会有人来找我做测试了，这是断我财路。
	3. 大智若愚。
3. 故事二：职责别人浪费与反思自己小气。
	1. 把职责对方浪费改为请求对方理解我的小气和我现在的焦虑。更容易达成目的。
	2. 迁怒：把自己的不快，莫名其妙地迁移到了别人身上。
	3. 要学会“**转念一想**”
4. 转念的习惯容易让我们走出思维的误区，改变我们独断偏执的思维性格。

## 三、反思力训练
1. 一些基本原则
	1. 经常性原则：让反思成为生活习惯，时时事事、有意无意之中都要反思。
	2. 正式性原则：采用文字、录音、他人监督等正式方式反思比随便想想更有效。
		1. 坚持写思维日记
		2. 动笔对于思维训练的重要性
		3. 更严谨、更深入、更持久的反思
	4. 沟通性原则：与当事人、同行、专家等他人交流。比自闭型反思更加全面更加客观
	5. 方法性原则：最好能综合运用各种方法进行反思，反思才能做到全面深入。
2. 常用的反思方法：

| 序号 | 方法       | 口号         | 操作                                                            |
| ---- | ---------- | ------------ | --------------------------------------------------------------- |
| 1    | 逆向反思法 | 未必就如此！ | 这个结论未必对！推理未必就成立！<br/>信息未必就可靠或全面       |
| 2    | 理由反思法 | 理由是什么？ | 他为什么这样说？我为什么这样想？<br/>这样说、这样想的根据何在？ |
| 3    | 利弊反思法 | 后果会怎样？ | 这样想、这样做会导致哪些好的、<br/>坏的结果？程度如何？         |
| 4    | 要素反思法 | 拆开来看看。 | 逐一反思我们这样想的目的、问题、<br/>概念、信息……               |
| 5    | 对比反思法 | 一比见好坏。 | 有没有别的可能方案？各种方案的优劣<br/>如何？别人可能会怎么想？ |

3. 举例一：曾国藩的“日课”。做得好的，做得不好的，都加以分析。（要养成反思的习惯）
4. 举例二：老师的课后反思
	1. “精彩”在哪里？为什么精彩？原因何在？
	2. “灵感”和意外的“偶得”有哪些？补充记录了吗？
	3. 问题（讲错、卡壳、沉闷等）有哪些？原因何在？如何改进？
	4. 教学内容是否系统全面、重点突出？
	5. 整堂课逻辑是否严密、紧凑？
	6. 课程内容对学生有没有真正的启发和帮助？
	7. 学生的课堂反应如何？课后有什么建议？
5. 智慧=经验+反思。
	1. 吃一堑、反思、长一智
	2. 人要在反思中不断成长
6. 举例三：学生的课后反思
	1. 这堂课老师着重讲了哪几个问题？这几个问题之间的逻辑关系如何？
	2. 本讲所涉及的核心概念的内涵和外延，我能准确地复述和解释吗？
	3. 本讲的观点、知识、规律、公式是怎样发现和推导出来的？意义何在？
	4. ？
	5. ？
	6. 对于老师课堂上所讲的，我能做出哪些补充或反补？（本堂课程，老师所说的不一定都是对的，所有内容只是老师的所想和心得。不是普遍理论更不是真理）
	7. 我需要复习教材或笔记中的哪些内容？
	8. 我是否需要和老师或同学就关联一点难点进行讨论？
	9. 练习题我如何用更简单的方法解出来？如何借助网络或图书馆展开进一步的拓展性的学习和研究？
7. 反思练习题：下面这些话的问题何在？
	1. 原句：
		1. 妈妈对儿子：“你给我认证写作业！”
		2. 老婆对老公说：“我累死了，要不是为了你的生日，我才不想出去了呢。”（背景补充：过年期间过生日，事情确实很多。过生日的酒店已经预订好，出去吃饭是一个必然的事情）
		3. 老婆对老公说：“你要多锻炼锻炼。否则将来瘫痪在床，谁来伺候你？！”
		4. 舍友：“注意，不要碰到我的网线。”
		5. 教室里，一个同学：“谁偷了我的一本逻辑书？请快点还给我。”
		6. 老师针对学生的提问：“别急，你这个问题，我待会儿会讲的。”
		7. 公司同事：“请给我一个表格，挺简单的。”
		8. “你说说是很容易。”
		9. 开始回应别人：“不对，……”、“不是的，……”。
		10. “你为什么这么自私？”
	2. 反思后：
		1. “请认证写你的作业！”
		2. “虽然今天已经很累了，但我还是很高兴能出去为老公过生日。”
			1. 既然出去吃饭是一个必然的事情，与其用一个负面的情绪去看待，不如换一个心情
			2. 原句说出来，会让过生日的老公心理很不舒服。这个结果是你抱怨的目的吗？显然不是
			3. 用一个好的心态去阐述自己的疲惫，说出去后你也能达成表达和宣泄，你心情会变好，听的人也会开心，大家都会更开心，你也会更开心
		3. “你要多锻炼身体。否则将来身体垮了，你的生活质量会大大下降，晚年的清福也享受不到。多可惜”
			1. 不要把人当做工具
			2. 人锻炼身体的目的不应该是为了完成某件事情，这样人就是完成这件事情的工具。锻炼身体有好处，就坦言即可
			3. 说话的目的是要让老公锻炼身体，而不是为了证明自己关心老公。所以要多反思说出去的话能否有助于达成这个目的。
			4. 要真正站在别人的角度为别人考虑问题，只有这样，别人才有可能接受你的劝告。
		4. “注意，不要被我的网线绊到了。”
			1. 原句是一种“自私自利”的角度，舍友没有义务考虑你自己的利益
			2. 站在对方角度
		5. “谁无意中错拿了我的一本逻辑书？请检查一下看看。谢谢。”
			1. 原句是一种“自我证实的预言”，在你说出这句话后，即便别人是不小心拿错的，现在也不敢归还你。
			2. 说话之前还是要多反思，不能被情绪牵着走。情绪首先把判断往偷的方向牵引了，但实时可能并非如此。
			3. 目的是为了拿回书，而不是为了宣泄情绪。
		6. “你的这个问题很好，这正是我过一会儿要讲的。”
			1. 原句说法是不礼貌的，而且会打击同学提问的积极性。
			2. 注意这种句式：“别急，……”。其实着急的是你自己，你却说别人别急。这是一种不好的习惯句式。听到这个句式的人，无论自己是不是真着急，都不会有好心情。
		7. “请给我做个表格，凭你的水平，相信用不了几分钟就能轻松搞定。”
			1. 我的反思：我也会经常跟下属说，“这个东西没这么复杂。”，但这样的话说出去后，听到的人会很不舒服。做出来了是因为这个东西很简单、做不出来就是简单的东西都做不好。这句话其实很伤人。
		8. “你说得很好，但是能否具体说说，我该怎么做呢？”
			1. 原句的情景很可能是在讨论一些问题，对方说出来的方案我在直觉上应该觉得是对的，但是很宽泛和空洞，很难具体执行。（错应该没错，就是不太可执行）
			2. 如果“你说的很对，但我做起来很难”。这样容易给人一种距离感，“我”和“你”之间的距离感。
		9. “你说的这一点很对，但我觉得应该考虑到……”
			1. 直接否定对方，会引起对方的警戒。你的否定很可能会招来对方下一轮的否定。谈话的氛围就会变得紧张起来。
			2. 很多时候我们的目的并不是为了否定对方，而是为了补充对方。但是原句的句式上来就开始全盘否定，对方肯定就不会好好跟你讨论了。
			3. 批判应该要从肯定和接受开始，而不应该从一棒子打死开始。
			4. 一棒子打死对方，对方肯定不会倾听和接受你的分析。
		10. “你能不能考虑一下我的要求/利益/……”（具体的利益和要求，不要讲空洞的要求）
			1. 原句是自我证实的预言。你在指责别人自私的时候，最后会激发别人的自私。你都说我自私了，我大概率会选择真的自私。
			2. 当你认为别人自私时，对方也许不是真的要损害你的利益，只是没有充分考虑到你的诉求而已。

## 四、总结
1. 在日常生活中，养成时时事事反思的习惯
2. 不时的反思一下我的思考和思考的我`

module.exports = {
  title: '反思',
  author: 'gzshi',
  post: '2023-06-08 22:50',
  tags: ['博弈', '管理', '胡思乱想'],
  summary: md,
  summaryCompiler: 'md',
  more: false
}


// WEBPACK FOOTER //
// ./src/blog/20230608_critical_thinking/p5.js